import React from 'react';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Tooltip,
} from '@heroui/react';
import { ResponsiveModal, ResponsiveTooltip, Typography } from '../../../../components';
import { useTranslations } from '../../../../contexts/translation.context';
import { RevshareCampaign } from '../types';
import { Icon } from '@iconify/react';
import { SYSTEM_FEE_PERCENT } from '../../offers/constants';

interface CampaignStatsModalProps {
  isOpen: boolean;
  onClose: () => void;
  campaign: RevshareCampaign;
}

const CampaignStatsModal: React.FC<CampaignStatsModalProps> = ({ isOpen, onClose, campaign }) => {
  const { t } = useTranslations();

  const formatCurrency = (value: number | string) => {
    const num = Number(value) || 0;
    return `$${num.toFixed(3)}`;
  };

  const formatPercent = (value: number) => {
    if (!isFinite(value) || isNaN(value)) return '0%';
    return `${value.toFixed(2)}%`;
  };

  // Get campaign data
  const campaignName = campaign.name || campaign.model?.name || campaign._id?.slice(-4) || '';
  const clicks = campaign.model?.transitions_count || 0;
  const subscribers = campaign.subscribed_count || 0;
  const transactionsTotal = Number(campaign.transactions_statistic?.total || 0);
  const transactionsCount = campaign.transactions_statistic?.count || 0;

  // Commission-based revenue calculation (partner's share, minus system fee)
  const commission = Math.max((campaign.offer?.commission || 0) - SYSTEM_FEE_PERCENT, 0);
  const revenue = transactionsTotal * (commission / 100);

  // Calculated metrics
  const ctr = clicks > 0 ? (subscribers / clicks) * 100 : 0;
  const arpu = subscribers > 0 ? transactionsTotal / subscribers : 0;
  const cvpc = clicks > 0 ? transactionsTotal / clicks : 0;
  const rpc = clicks > 0 ? revenue / clicks : 0;

  // Transaction tiers from statistics
  const tier1 = campaign.transactions_statistic?.[1] || 0;
  const tier2 = campaign.transactions_statistic?.[2] || 0;
  const tier500 = campaign.transactions_statistic?.[500] || 0;
  const chats = campaign.subs?.with_gte_3_chat_messages_count || 0;

  // Calculate percentages for tiers (based on subscribers or transactions count)
  const calculatePercent = (count: number, total: number) => {
    if (total === 0) return 0;
    return (count / total) * 100;
  };

  // Stats row component
  const StatRow = ({ icon, label, value }: { icon: string; label: string; value: string }) => (
    <div className="flex items-center ">
      <div className="flex flex-1 gap-2 items-center">
        <Icon icon={icon} className="w-4 h-4 text-auth-muted shrink-0" />
        <span className="text-xs text-auth-muted leading-4 max-w-[25rem]">{label}</span>
      </div>
      <span className="text-xs text-auth-ink leading-4">{value}</span>
    </div>
  );

  // Category row component for the table
  const CategoryRow = ({
    label,
    count,
    percent,
    hasTooltip,
    tooltipText,
  }: {
    label: string;
    count: number;
    percent: number;
    hasTooltip?: boolean;
    tooltipText?: string;
  }) => (
    <div className="flex items-center py-1.5 text-xs">
      <div className="flex-1 flex items-center gap-0.5">
        <span className="text-auth-muted leading-4">{label}</span>
        {hasTooltip && tooltipText && (
          <ResponsiveTooltip content={tooltipText}>
            <Icon icon="solar:info-circle-bold" className="w-4 h-4 text-neutral-8 cursor-pointer" />
          </ResponsiveTooltip>
        )}
      </div>
      <span className="w-[9rem] text-auth-ink font-medium leading-4">{count}</span>
      <span className="w-[9rem] text-auth-ink font-medium leading-4">{formatPercent(percent)}</span>
    </div>
  );

  return (
    <ResponsiveModal
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      hideCloseButton
      classNames={{
        base: 'md:max-w-[37.5rem]',
        backdrop: 'bg-black/50',
      }}
    >
      <ModalContent className="rounded-4 shadow-[0px_0px_11px_0px_rgba(0,0,0,0.1)]">
        <ModalHeader className="flex flex-col gap-3 px-4 pt-5 pb-4 border-b border-auth-surface-hover relative">
          <Typography variant="label1" className="font-medium text-auth-ink text-base pr-8">
            {t('partner.revshare.campaigns.statsModal.title') || 'Статистика кампании'} "
            {campaignName}"
          </Typography>
          <button
            onClick={onClose}
            className="absolute top-2 right-2 w-8 h-8 flex items-center justify-center rounded-2 hover:bg-gray-100"
          >
            <Icon icon="lucide:x" className="w-4 h-4 text-auth-muted" />
          </button>
        </ModalHeader>

        <ModalBody className="px-4 py-0">
          {/* Stats section */}
          <div className="border-b border-[#E2EBF7] py-4">
            <div className="flex flex-col gap-2">
              <StatRow
                icon="solar:cursor-square-linear"
                label={t('partner.revshare.campaigns.statsModal.clicks') || 'Клики'}
                value={clicks.toString()}
              />
              <StatRow
                icon="solar:users-group-two-rounded-linear"
                label={t('partner.revshare.campaigns.statsModal.subscribers') || 'Подписчики'}
                value={subscribers.toString()}
              />
              <StatRow
                icon="solar:banknote-linear"
                label={
                  t('partner.revshare.campaigns.statsModal.totalTransactions') || 'Всего транзакций'
                }
                value={formatCurrency(transactionsTotal)}
              />
              <StatRow
                icon="solar:card-recive-linear"
                label={t('partner.revshare.campaigns.statsModal.revenue') || 'Доход'}
                value={formatCurrency(revenue)}
              />
              <StatRow
                icon="solar:sale-square-linear"
                label={
                  t('partner.revshare.campaigns.statsModal.ctr') ||
                  'CTR (конверсия кликов в подписчиков)'
                }
                value={formatPercent(ctr)}
              />

              <StatRow
                icon="solar:user-check-linear"
                label={
                  t('partner.revshare.campaigns.statsModal.arpu') ||
                  'ARPU - Средний доход на одного пользователя (общая сумма транзакций / количество поклонников)'
                }
                value={formatCurrency(arpu)}
              />
              <StatRow
                icon="solar:dollar-minimalistic-linear"
                label={
                  t('partner.revshare.campaigns.statsModal.cvpc') ||
                  'CVPC - Стоимость клиента за клик (общая сумма транзакций / количество кликов)'
                }
                value={formatCurrency(cvpc)}
              />

              <StatRow
                icon="solar:hand-money-linear"
                label={
                  t('partner.revshare.campaigns.statsModal.rpc') ||
                  'RPC - Доход за клик (доход / количество кликов)'
                }
                value={formatCurrency(rpc)}
              />
            </div>
          </div>

          {/* Category table */}
          <div className="py-4">
            {/* Table header */}
            <div className="flex items-center px-0 py-1.5 text-xs font-medium text-auth-muted">
              <span className="flex-1 leading-4">
                {t('partner.revshare.campaigns.statsModal.fanCategory') || 'Категория фана'}
              </span>
              <span className="w-[9rem] leading-4">
                {t('partner.revshare.campaigns.statsModal.fansCount') || 'Фанатов'}
              </span>
              <span className="w-[9rem] leading-4">%</span>
            </div>

            {/* Table rows */}
            <div className="flex flex-col">
              <CategoryRow
                label={
                  t('partner.revshare.campaigns.statsModal.paidSubscription') || 'Платная подписка'
                }
                count={tier1}
                percent={calculatePercent(tier1, subscribers)}
              />
              <CategoryRow
                label={
                  t('partner.revshare.campaigns.statsModal.recurringSubscription') ||
                  'Оплата повторяющейся подписки'
                }
                count={tier2}
                percent={calculatePercent(tier2, subscribers)}
              />
              <CategoryRow
                label={t('partner.revshare.campaigns.statsModal.purchases') || 'Покупки'}
                count={transactionsCount}
                percent={calculatePercent(transactionsCount, subscribers)}
              />
              <CategoryRow
                label={t('partner.revshare.campaigns.statsModal.purchases500') || 'Покупки на $500'}
                count={tier500}
                percent={calculatePercent(tier500, subscribers)}
              />
              <CategoryRow
                label={t('partner.revshare.campaigns.statsModal.messages') || 'Сообщения'}
                count={chats}
                percent={calculatePercent(chats, subscribers)}
                hasTooltip
                tooltipText={
                  t('partner.revshare.campaigns.statsModal.messagesTooltip') ||
                  'Количество подписчиков с 3+ сообщениями в чате'
                }
              />
            </div>
          </div>
        </ModalBody>

        <ModalFooter className="px-4 md:px-6 py-4 flex justify-end">
          <Button className="bg-partner h-10 px-3.5 py-2.5 rounded-3" onPress={onClose}>
            <Typography variant="body2" className="!text-white font-semibold">
              {t('partner.revshare.campaigns.statsModal.close') || 'Закрыть'}
            </Typography>
          </Button>
        </ModalFooter>
      </ModalContent>
    </ResponsiveModal>
  );
};

export default CampaignStatsModal;
